import { makeStyles } from "@fluentui/react-components";
import {
  GREEN_INK,
  GREEN_SOFT_BG,
} from "apps/project-iq/utils/diagnosticUtils";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

export const useDiagnosticResultFilesStyles = makeStyles({
  root: {
    padding: "28px",
    fontFamily: projectIqTokens.fontFamily,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: projectIqTokens.card,
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px",
  },

  title: {
    fontSize: "20px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  completeBadge: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: GREEN_INK,
    backgroundColor: GREEN_SOFT_BG,
    padding: "4px 10px",
    borderRadius: "6px",
    lineHeight: "1",
  },

  subtitle: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "4px",
    marginBottom: "20px",
  },

  sectionLabel: {
    fontSize: "11px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.faint,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    margin: `0 0 ${designTokens.spacing[2]} 0`,
  },

  dropZone: {
    border: `2px dashed ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: `${designTokens.spacing[8]} ${designTokens.spacing[4]}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[2],
    cursor: "pointer",
    textAlign: "center",
    marginBottom: designTokens.spacing[4],
    ":hover": {
      border: `2px dashed ${projectIqTokens.tealLine}`,
      backgroundColor: projectIqTokens.bg,
    },
  },

  dropZoneActive: {
    border: `2px dashed ${projectIqTokens.teal}`,
    backgroundColor: projectIqTokens.bg,
  },

  dropZoneDisabled: {
    opacity: 0.5,
    pointerEvents: "none",
    cursor: "default",
  },

  dropZoneIcon: {
    fontSize: "28px",
    color: projectIqTokens.teal,
    marginBottom: designTokens.spacing[1],
  },

  dropZoneTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  dropZoneHint: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
    margin: 0,
  },

  emptyStateNonOwner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${designTokens.spacing[10]} ${designTokens.spacing[4]}`,
    color: projectIqTokens.faint,
    fontSize: designTokens.fontSize.sm,
    textAlign: "center",
  },

  fileList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.card,
    gap: designTokens.spacing[3],
    minWidth: 0,
    overflow: "hidden",
  },

  fileInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },

  fileIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: designTokens.borderRadius.md,
    flexShrink: 0,
  },

  fileIconPdf: {
    backgroundColor: "rgba(194,85,63,0.12)",
    color: projectIqTokens.rust,
  },

  fileIconXls: {
    backgroundColor: "rgba(47,158,110,0.12)",
    color: projectIqTokens.green,
  },

  fileIconPpt: {
    backgroundColor: "rgba(217,138,43,0.12)",
    color: projectIqTokens.amber,
  },

  fileIconDefault: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.ink3,
  },

  fileName: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    margin: 0,
  },

  fileMeta: {
    fontSize: designTokens.fontSize.xxs,
    color: projectIqTokens.faint,
    margin: 0,
  },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
  },

  errorMessage: {
    color: designTokens.semantic.error,
    fontSize: designTokens.fontSize.sm,
    margin: `${designTokens.spacing[2]} 0 0 0`,
  },

  hiddenInput: {
    display: "none",
  },

  spinnerRoot: {
    display: "flex",
    justifyContent: "center",
    padding: designTokens.spacing[10],
  },

  fileRowUploading: {
    opacity: 0.5,
    pointerEvents: "none",
  },

  fileSpinner: {
    flexShrink: 0,
  },

  furtherAnalysisSection: {
    marginTop: designTokens.spacing[6],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },

  analysisLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: designTokens.spacing[6],
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: projectIqTokens.card,
  },

  analysisCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[4],
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    "--arrow-color": projectIqTokens.faint,
    transition: `box-shadow ${designTokens.transition.fast}`,
    ":hover": {
      boxShadow: `0 0 0 1px ${projectIqTokens.tealLine}, ${projectIqTokens.shadow}`,
      "--arrow-color": projectIqTokens.teal,
    },
  },

  analysisIconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    minWidth: "36px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.teal,
    fontSize: "18px",
    flexShrink: 0,
  },

  analysisContent: {
    flex: 1,
    minWidth: 0,
  },

  analysisTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: `0 0 2px 0`,
  },

  analysisSubtitle: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    margin: 0,
  },

  analysisLookupError: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    marginBottom: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    border: `1px solid ${projectIqTokens.rust}`,
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
    fontSize: designTokens.fontSize.sm,
  },

  analysisArrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--arrow-color)" as string,
    flexShrink: 0,
    fontSize: "20px",
  },

  divider: {
    border: "none",
    borderTop: `1px solid ${projectIqTokens.line}`,
    margin: `${designTokens.spacing[5]} 0`,
  },

  summarySection: {
    marginTop: designTokens.spacing[6],
  },

  summaryTextarea: {
    width: "100%",
    minHeight: "100px",
    padding: designTokens.spacing[3],
    fontSize: designTokens.fontSize.sm,
    fontFamily: "inherit",
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    resize: "vertical",
    boxSizing: "border-box",
    color: projectIqTokens.ink,
    backgroundColor: projectIqTokens.card,
    ":focus": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "-1px",
    },
  },

  summaryActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: designTokens.spacing[2],
  },

  completeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[3],
    flexWrap: "wrap",
  },

  completeHint: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    flex: 1,
    margin: 0,
  },

  draftPanel: {
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
    marginBottom: designTokens.spacing[3],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },

  analysisCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
  },

  analyticsTabBar: {
    display: "flex",
    gap: designTokens.spacing[1],
    borderBottom: `1px solid ${projectIqTokens.line}`,
    marginBottom: designTokens.spacing[4],
  },

  analyticsTab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    marginBottom: "-1px",
    transition: designTokens.transition.fast,
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },

  analyticsTabActive: {
    borderBottom: `2px solid ${projectIqTokens.teal}`,
    color: projectIqTokens.ink,
  },

  draftScroll: {
    maxHeight: "240px",
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    fontSize: designTokens.fontSize.sm,
    lineHeight: designTokens.fontSize.sm,
    wordWrap: "break-word",
    padding: designTokens.spacing[3],
    backgroundColor: projectIqTokens.bg,
    borderRadius: designTokens.borderRadius.md,
  },

  progressList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  progressStep: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    color: projectIqTokens.ink,
    fontSize: designTokens.fontSize.sm,
  },

  draftActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  draftError: {
    color: designTokens.semantic.error,
    fontSize: designTokens.fontSize.sm,
    margin: 0,
  },
});
